import { BlockStack, Layout, LegacyCard, LegacyStack, Text } from '@shopify/polaris'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Result } from '../common/api'
import { MemoedTextField, resultErrorField, useQuery } from '../common/helpers'
import { useSaveBarContext } from '../common/SaveBar'
import * as urls from '../common/urls'
import { SaveBar } from '../main/SaveBarProvider'
import { ActionsCard } from './ActionsCard'
import { WorkflowFormData } from './api'
import { FiltersCard, workflowFilterSections } from './FiltersCard'
import { TriggersCard } from './TriggersCard'
import { WorkflowDescription } from './WorkflowDescription'
import { WorkflowStatusBadge } from './WorkflowStatusBadge'

interface WorkflowFormProps {
  initialData: WorkflowFormData
  onSave: (data: WorkflowFormData) => any
  isSaving: boolean
  saveResult: Result<any> | null
  isNew: boolean
}

export const WorkflowForm = (props: WorkflowFormProps) => {
  const query = useQuery()
  const spgId = query.get('spg_id')

  const { hideAndRedirect } = useSaveBarContext()
  const { initialData, saveResult } = props
  const [data, setData] = useState<WorkflowFormData>(initialData)
  useEffect(() => setData(initialData), [initialData])

  const isDirty = useMemo(
    () => JSON.stringify(data) !== JSON.stringify(initialData),
    [data, initialData]
  )

  const handleDiscard = useCallback(() => {
    setData(initialData)
    if (props.isNew) {
      hideAndRedirect(spgId ? urls.planUrl(spgId) : urls.workflowsUrl, 'replace')
    }
  }, [hideAndRedirect, props.isNew, initialData, spgId])

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="300">
          <LegacyCard title="Name">
            <LegacyCard.Section>
              <MemoedTextField
                name="name"
                type="text"
                label="Name"
                labelHidden
                value={data.name}
                onChange={(val) => setData({ ...data, name: val })}
                autoComplete="off"
                error={resultErrorField(saveResult, 'name') ?? undefined}
              />
            </LegacyCard.Section>
          </LegacyCard>
          <TriggersCard
            triggers={data.triggers}
            onChange={(triggers) => setData({ ...data, triggers })}
            error={resultErrorField(saveResult, 'triggers')}
          />
          <FiltersCard
            filters={data.filters}
            onChange={(filters) => setData({ ...data, filters })}
            rootFilterType={data.rootFilterType}
            onRootFilterTypeChange={(rootFilterType) => setData({ ...data, rootFilterType })}
            workflowFilterOptions={workflowFilterSections()}
          />
          <ActionsCard
            data={data}
            onChange={(actions) => setData({ ...data, actions })}
            saveResult={saveResult}
          />
        </BlockStack>
      </Layout.Section>
      <Layout.Section variant="oneThird">
        <BlockStack gap="400">
          <LegacyCard sectioned>
            <LegacyStack distribution="equalSpacing">
              <Text variant="headingMd" as="h2">
                Summary
              </Text>
              <WorkflowStatusBadge data={data} />
            </LegacyStack>
            <br />
            <WorkflowDescription
              filters={data.filters}
              actions={data.actions}
              triggers={data.triggers}
              rootFilterType={data.rootFilterType}
            />
          </LegacyCard>
        </BlockStack>
      </Layout.Section>
      <SaveBar
        isNew={props.isNew}
        isDirty={isDirty}
        onSave={() => props.onSave(data)}
        onDiscard={handleDiscard}
        isSubmitting={props.isSaving}
        isSaveDisabled={!isDirty}
      />
    </Layout>
  )
}
